(() => {
  if (window.DDTracking) return;
  const config = document.querySelector('[data-dd-event-tracking]');
  if (!config || config.dataset.enabled !== 'true') return;
  const params = new URLSearchParams(location.search);
  const debug = params.get('dd_tracking_debug') === '1';
  const dryRun = config.dataset.live !== 'true' || config.dataset.editor === 'true' || debug;
  const records = [];
  const status = { dryRun, published: 0, unavailable: 0, errors: 0 };
  const clean = (value) => String(value || '').replace(/\s+/g, ' ').trim().slice(0, 160);
  const snakeCase = (key) => key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  const normalize = (values) => Object.fromEntries(Object.entries(values).map(([key, value]) => [snakeCase(key), value]));
  const base = () => ({ pagePath: location.pathname, pageType: config.dataset.pageType, currency: config.dataset.currency, schemaVersion: 1 });
  const emit = (name, properties = {}) => {
    try {
      const data = normalize({ ...base(), ...properties });
      if (dryRun) {
        records.push({ name, data });
        if (records.length > 100) records.shift();
        if (debug) document.dispatchEvent(new CustomEvent('dd:tracking-debug', { detail: { name, data } }));
        return;
      }
      if (typeof window.Shopify?.analytics?.publish !== 'function') { status.unavailable++; return; }
      window.Shopify.analytics.publish(name, data);
      status.published++;
    } catch (_) { status.errors++; }
  };
  const context = (element) => {
    const modal = element?.closest('quick-add-modal');
    const ownerId = modal?.dataset.ddCardPurchaseOwner || modal?.dataset.ddCardQuickAddOwner;
    const owner = ownerId ? document.getElementById(ownerId) : element;
    const anchor = owner || element;
    const section = anchor?.closest('.shopify-section');
    const carousel = anchor?.closest('dd-continuous-carousel, dd-carousel, slider-component');
    const card = anchor?.closest('.dd-mini-cart-recommendation-card, .dd-feature-product-card, .dd-product-card, .card-wrapper');
    const item = card?.closest('[data-dd-continuous-item], [data-dd-carousel-item]') || card;
    let position = Number(card?.dataset.ddTrackingPosition) || 0;
    if (!position && item?.parentElement) {
      const siblings = [...item.parentElement.children].filter((node) => !node.hasAttribute('data-dd-loop-clone'));
      position = siblings.indexOf(item) + 1;
      if (position <= 0) position = 0;
    }
    const placement = anchor?.closest('cart-drawer') ? 'mini_cart' :
      anchor?.closest('dd-cart-page, cart-items') ? 'cart' :
      anchor?.closest('dd-pdp-product-form') ? 'product_form' :
      carousel ? 'carousel' : card && config.dataset.pageType === 'product' ? 'product_page_quick_add' :
      card ? 'product_card' : 'product_form';
    return {
      placement, sectionId: clean(section?.id), sectionName: clean(section?.querySelector('h2')?.textContent),
      position: Number(anchor?.closest('[data-dd-loop-clone]')?.dataset.ddLoopCloneIndex) + 1 || position, pageProductId: config.dataset.productId || '',
      productId: card?.dataset.ddTrackingProductId || '',
    };
  };
  const add = (items, source, extra = {}) => {
    const list = Array.isArray(items) ? items : [items];
    list.forEach((item) => {
      const quantity = Number(item?.addedQuantity ?? item?.quantity);
      const variantId = item?.variant_id || item?.variantId || item?.id;
      if (!variantId || !Number.isFinite(quantity) || quantity <= 0) return;
      const price = Number(item.price ?? item.unitPrice);
      const value = Number.isFinite(price) ? price * quantity / 100 : 0;
      const sourceContext = context(source);
      emit('dd_add_success', {
        ...sourceContext, ...extra, productId: String(item.product_id || item.productId || sourceContext.productId || ''),
        additionalProduct: item.rowType ? item.rowType !== 'current' : sourceContext.placement === 'product_page_quick_add',
        variantId: String(variantId), quantity, value,
      });
    });
  };
  const cartQuantity = (source, cart, key, variantId, before, cartContext) => {
    if (!Array.isArray(cart?.items) || !Number.isFinite(before)) return;
    const item = (key && cart.items.find((line) => line.key === key)) ||
      cart.items.find((line) => String(line.variant_id) === String(variantId));
    const after = Number(item?.quantity || 0);
    if (after === before) return;
    const sourceContext = context(source);
    emit('dd_cart_quantity_changed', {
      ...sourceContext, placement: cartContext === 'drawer' ? 'mini_cart' : cartContext === 'page' ? 'cart' : sourceContext.placement,
      productId: String(item?.product_id || ''), variantId: String(variantId || item?.variant_id || ''),
      quantityBefore: before, quantityAfter: after, quantityDelta: after - before,
      direction: after > before ? 'increase' : after === 0 ? 'remove' : 'decrease',
    });
  };
  window.DDTracking = { emit, context, add, cartQuantity, status, records };
  document.addEventListener('dd:pdp-added', (event) => {
    const lines = event.detail?.lines || [];
    const returned = event.detail?.cartData?.items || [];
    add(lines.map((line) => ({ ...returned.find((item) => String(item.variant_id || item.id) === String(line.variantId)), ...line })), event.target,
      { selectedUnits: lines.reduce((total, line) => total + Number(line.quantity || 0), 0) });
  });
  document.addEventListener('click', (event) => {
    const link = event.target?.closest?.('a[href]');
    if (!link || link.closest('[data-dd-exit-intent]')) return;
    let region = '';
    if (link.closest('header, header-drawer, [data-dd-header]')) region = 'header';
    else if (link.closest('footer, [data-dd-footer]')) region = 'footer';
    else if (config.dataset.pageType === 'index' && link.closest('main')) region = 'homepage';
    if (!region) return;
    try {
      const url = new URL(link.href, location.href);
      if (!['http:', 'https:'].includes(url.protocol) || (url.pathname === location.pathname && url.hash)) return;
      emit('dd_link_clicked', { ...context(link), region,
        label: clean(link.getAttribute('aria-label') || link.textContent || link.querySelector('img')?.alt),
        destination: url.origin === location.origin ? url.pathname : url.origin + url.pathname });
    } catch (_) {}
  }, { passive: true });
  const observeFeatures = () => {
    if (!window.IntersectionObserver) return;
    const features = document.querySelectorAll('[data-dd-pdp-additional-flavours]');
    if (!features.length) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
        emit('dd_feature_viewed', { ...context(entry.target), feature: 'pdp_additional_products' });
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.5 });
    features.forEach((feature) => observer.observe(feature));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observeFeatures, { once: true });
  else observeFeatures();
})();
